import React, { useCallback, useMemo, useState } from "react";
import api from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";
import { useQueryClient } from "@tanstack/react-query";
import { downloadCsv } from "@/lib/csv";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { NoProjectAccess } from "@/components/AppShell";
import { FaultDetail } from "@/components/FaultDetail";
import { FaultTrends } from "@/components/FaultTrends";
import { usePersistentState } from "@/lib/usePersistentState";
import { FaultRow } from "@/components/FaultRow";
import { FaultCostRanking } from "@/components/FaultCostRanking";
import { FaultTasks, invalidateFaultWork } from "@/components/FaultTasks";
import { FaultSavings } from "@/components/FaultSavings";
import { FaultsKpiStrip } from "@/components/FaultsKpiStrip";
import { FaultsToolbar } from "@/components/FaultsToolbar";
import { shiftDays } from "@/lib/faultFormat";
import { toast } from "sonner";
import { Download, RefreshCcw } from "lucide-react";

const PRESETS = [
  { label: "This week", days: 7, mode: "week" },
  { label: "Last 7 days", days: 7, mode: "rolling" },
  { label: "Last 30 days", days: 30, mode: "rolling" },
];

function weekStart(d) {
  const copy = new Date(d);
  copy.setDate(copy.getDate() - copy.getDay());
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function iso(d) {
  return d.toISOString().slice(0, 10);
}

function rangeLabel(startIso, days, mode) {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  const fmt = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  if (mode === "week") return `Week of ${fmt(start)} ${start.getFullYear()}`;
  return `${fmt(start)} – ${fmt(end)} ${end.getFullYear()}`;
}

function deriveFilters(rows) {
  const seen = new Map();
  const siteMap = new Map();
  rows.forEach((r) => {
    if (!seen.has(r.rule_id)) seen.set(r.rule_id, { rule_id: r.rule_id, rule: r.rule });
    siteMap.set(r.site_id, r.site_dis);
  });
  return {
    rules: Array.from(seen.values()).sort((a, b) => a.rule.localeCompare(b.rule)),
    sites: Array.from(siteMap.entries())
      .map(([id, dis]) => ({ id, dis }))
      .sort((a, b) => (a.dis || "").localeCompare(b.dis || "")),
  };
}

export default function FaultsPage() {
  const { user } = useAuth();
  const { project } = useProject();
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState(PRESETS[0]);
  const [start, setStart] = useState(iso(weekStart(new Date())));
  const [site, setSite] = usePersistentState("koioc_faults_site", "");
  const [rule, setRule] = usePersistentState("koioc_faults_rule", "");
  const [costOnly, setCostOnly] = usePersistentState("koioc_faults_cost_only", false);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const params = useMemo(
    () => ({ start, days: preset.days, cost_only: costOnly, ...(project ? { project } : {}) }),
    [start, preset.days, costOnly, project]
  );
  const { data, loading, err, refetch } = useApiQuery(["faults", params], "/faults", { params });

  const { rules, sites } = useMemo(() => deriveFilters(data?.rows || []), [data]);

  /** Rebuild from the analytics feed, then re-read the freshly built result. */
  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.get("/faults", { params: { ...params, refresh: true } });
      await refetch();
      toast.success("Faults refreshed from the analytics feed");
    } catch (e) {
      toast.error("Refresh failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRefreshing(false);
    }
  };

  const rows = useMemo(() => {
    let out = data?.rows || [];
    if (site) out = out.filter((r) => r.site_id === site);
    if (rule) out = out.filter((r) => r.rule_id === rule);
    return out;
  }, [data, site, rule]);

  const totals = useMemo(
    () => ({
      count: rows.length,
      hours: rows.reduce((a, r) => a + r.hours, 0),
      cost: rows.reduce((a, r) => a + (r.cost || 0), 0),
      critical: rows.filter((r) => r.critical).length,
    }),
    [rows]
  );

  const maxHours = useMemo(() => Math.max(...rows.map((r) => r.hours), 0.0001), [rows]);

  const onPreset = useCallback((p) => {
    setPreset(p);
    setStart(p.mode === "week" ? iso(weekStart(new Date())) : shiftDays(iso(new Date()), -(p.days - 1)));
  }, []);

  const exportCsv = () =>
    downloadCsv(
      `faults_${start}_${preset.days}d.csv`,
      ["Site", "Rule", "Critical", "Hours", "Cost", "Equipment"],
      rows.map((r) => [r.site_dis, r.rule, r.critical ? "yes" : "no", r.hours, r.cost ?? "", (r.equips || []).join("; ")])
    );

  if (user?.role !== "admin" && (user?.projects || []).length === 0) return <NoProjectAccess />;

  return (
    <div className="max-w-7xl fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="k-label">Diagnostics</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">Faults</h1>
          <p className="mt-3 text-sm text-[#4D4D4D]">
            Every rule violation detected across your portfolio, how long it ran and what it cost.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} disabled={refreshing} data-testid="faults-refresh-btn" className="k-btn-outline">
            <RefreshCcw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button onClick={exportCsv} disabled={loading || rows.length === 0} data-testid="faults-export-btn" className="k-btn-outline">
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
      </div>

      <FaultsKpiStrip totals={totals} loading={loading} />

      <FaultCostRanking rows={rows} totalCost={totals.cost} onOpen={setSelected} onClaimed={() => invalidateFaultWork(queryClient)} />
      <FaultTasks />
      <FaultSavings />

      <FaultTrends />

      <FaultsToolbar
        presets={PRESETS}
        preset={preset}
        label={rangeLabel(start, preset.days, preset.mode)}
        onPrev={() => setStart(shiftDays(start, -preset.days))}
        onNext={() => setStart(shiftDays(start, preset.days))}
        onPreset={onPreset}
        sites={sites}
        site={site}
        onSite={setSite}
        rules={rules}
        rule={rule}
        onRule={setRule}
        costOnly={costOnly}
        onCostOnly={setCostOnly}
      />

      <div className="mt-4 k-card overflow-hidden" data-testid="faults-table">
        <div className="hidden lg:grid grid-cols-12 gap-3 px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="col-span-3 k-sublabel">Rule / Site</div>
          <div className="col-span-2 k-sublabel">Duration</div>
          <div className="col-span-1 k-sublabel text-right">Cost</div>
          <div className="col-span-4 k-sublabel">When it was active</div>
          <div className="col-span-2 k-sublabel">Equipment</div>
        </div>
        {loading ? (
          <div className="p-10 text-center k-sublabel">Reading faults from the analytics feed…</div>
        ) : err ? (
          <div className="p-6 text-sm text-red-600" data-testid="faults-error">{err}</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#6B7280]" data-testid="faults-empty">
            No faults detected for this period. Nice.
          </div>
        ) : (
          rows.map((r, i) => (
            <FaultRow key={`${r.site_id}-${r.rule_id}`} row={r} index={i} maxHours={maxHours} days={preset.days} startIso={data.start} onOpen={setSelected} />
          ))
        )}
      </div>

      {selected && (
        <FaultDetail
          fault={selected}
          start={data?.start}
          days={preset.days}
          isAdmin={user?.role === "admin"}
          onClose={() => setSelected(null)}
          onRuleChanged={() => {
            setSelected(null);
            refresh();
          }}
        />
      )}

      {data?.computed_at && (
        <p className="mt-3 text-xs text-[#9CA3AF]">
          Last read {new Date(data.computed_at).toLocaleString()} · cached and refreshed on the hourly sync.
        </p>
      )}
    </div>
  );
}
